import { getBundleStore } from '@theme/pdp-bundle-store';
import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';
import { onAnimationEnd, prefersReducedMotion } from '@theme/utilities';

/**
 * Custom element that renders the collapsible order summary on the PDP.
 *
 * Three refresh sources:
 *  - `variant:update` — refresh main product price + variant label (re-reads
 *    HTML fragment dispatched by variant-picker), then re-renders header total.
 *  - `model:update` — re-reads main price from dataset (morphed by Liquid),
 *    re-caches refs, re-renders.
 *  - `pdp:bundle:change` — pulls a snapshot from the bundle store and renders
 *    one breakdown row per contributor display item (bands, cross-sell, gift,
 *    subscription...). When at least one contributor has display items, the
 *    header price shows the bundle total (main + contributors); otherwise it
 *    shows the main product price alone.
 */
class OrderSummary extends HTMLElement {
  /** @type {string | undefined} */
  #productId;

  /** @type {Element | null} */
  #priceEl = null;

  /** @type {Element | null} */
  #variantLabelEl = null;

  /** @type {Element | null} */
  #breakdownEl = null;

  /** @type {Element | null} - The price shown next to the main product in the breakdown row. */
  #mainPriceLineEl = null;

  /**
   * Per-contributor, per-item rows. The inner map is keyed by a stable id
   * (`variantId` falls back to `title`) so rows can be added or removed
   * individually while siblings stay mounted — enabling per-item enter/leave
   * animations.
   *
   * @type {Map<string, Map<string, { row: HTMLLIElement, signature: string }>>}
   */
  #rowsByContributor = new Map();

  /** @type {number} */
  #mainPriceCents = 0;

  /** @type {string} */
  #mainPriceFormatted = '';

  /** @type {string} - Money format template injected from Liquid (`shop.money_format`). */
  #moneyFormat = '{{amount}}';

  /** @type {string} - ISO currency code injected from Liquid. */
  #currency = 'EUR';

  /** @type {Element | null} - Owning section, source of the `variant:selected` loading signal. */
  #section = null;

  connectedCallback() {
    this.#productId = this.dataset.productId;
    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.#currency = this.dataset.currency || 'EUR';
    this.#cacheElements();
    this.#cacheMainPrice();
    this.#section = this.closest('.shopify-section, dialog');
    document.addEventListener('variant:update', this.#onVariantUpdate);
    document.addEventListener('model:update', this.#onModelUpdate);
    document.addEventListener('pdp:bundle:change', this.#onBundleChange);
    this.#section?.addEventListener('variant:selected', this.#onVariantSelected);
    this.#syncHeaderPrice(getBundleStore().snapshot());
    this.#renderContributors(getBundleStore().snapshot());
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.#onVariantUpdate);
    document.removeEventListener('model:update', this.#onModelUpdate);
    document.removeEventListener('pdp:bundle:change', this.#onBundleChange);
    this.#section?.removeEventListener('variant:selected', this.#onVariantSelected);
  }

  /**
   * Dims the summary price while the variant fetch is in flight (from
   * `variant:selected` on click until `variant:update` swaps the fresh price).
   */
  #onVariantSelected = () => {
    this.classList.add('is-loading');
  };

  #cacheElements() {
    this.#priceEl = this.querySelector('[ref="price"]');
    this.#variantLabelEl = this.querySelector('[ref="variantLabel"]');
    this.#breakdownEl = this.querySelector('[ref="breakdown"]');
    this.#mainPriceLineEl = this.querySelector('[ref="mainPrice"]');
  }

  /**
   * Capture the main variant price (in cents + formatted string + format
   * template). Called on connect, after `model:update` (dataset is morphed),
   * and after `variant:update` (priceEl text is freshly updated).
   */
  #cacheMainPrice() {
    this.#mainPriceCents = Number(this.dataset.mainPriceCents) || 0;
    const text = this.#priceEl?.textContent?.trim() ?? '';
    if (text) this.#mainPriceFormatted = text;
  }

  /**
   * Update the collapsible header price: bundle total when contributors are
   * active, main variant price otherwise.
   *
   * @param {{ contributors: Array<{ display: Array<any> }>, totalCents: number }} snapshot
   */
  #syncHeaderPrice(snapshot) {
    if (!this.#priceEl) return;
    const hasContribs = snapshot.contributors.some((c) => c.display.length > 0);
    if (!hasContribs) {
      this.#priceEl.textContent = this.#mainPriceFormatted;
      return;
    }
    const total = this.#mainPriceCents + snapshot.totalCents;
    this.#priceEl.textContent = formatMoney(total, this.#moneyFormat, this.#currency);
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    this.classList.remove('is-loading');

    const detail = /** @type {any} */ (event).detail;
    if (!detail || detail.data?.productId !== this.#productId) return;

    const htmlDoc = detail.data?.html;
    if (!htmlDoc) return;

    // Source of truth = the freshly-rendered <order-summary> in the fetched
    // fragment (matched by section, product id as fallback). Liquid already
    // baked the correct variant label + price for the selected variant there.
    const sectionId = this.dataset.sectionId;
    const freshSummary =
      (sectionId && htmlDoc.querySelector(`order-summary[data-section-id="${sectionId}"]`)) ||
      htmlDoc.querySelector(`order-summary[data-product-id="${this.#productId}"]`);

    const freshPriceText =
      freshSummary?.querySelector('[ref="price"]')?.textContent?.trim() ||
      freshSummary?.querySelector('[ref="mainPrice"]')?.textContent?.trim() ||
      htmlDoc
        .querySelector(
          `product-price[data-product-id="${this.#productId}"] .price__regular:not(.price__hidden) .price, product-price[data-product-id="${this.#productId}"] .price__sale:not(.price__hidden) .price-item--sale`
        )
        ?.textContent?.trim() ||
      '';

    if (freshPriceText) {
      // Mirror the new main variant price into both the breakdown row and the
      // header (the header is then re-overwritten by #syncHeaderPrice below if
      // a bundle is active — so it ends up showing the correct total).
      if (this.#mainPriceLineEl) this.#mainPriceLineEl.textContent = freshPriceText;
      if (this.#priceEl) this.#priceEl.textContent = freshPriceText;
      this.#mainPriceFormatted = freshPriceText;
    }

    const freshVariantTitle =
      freshSummary?.querySelector('[ref="variantLabel"]')?.textContent?.trim() ||
      htmlDoc.querySelector(`[data-variant-title-for="${this.#productId}"]`)?.textContent?.trim();
    if (freshVariantTitle && this.#variantLabelEl) {
      this.#variantLabelEl.textContent = freshVariantTitle;
    }

    // Refresh cached cents. Prefer the price from the variant resource (most
    // reliable), then the fresh summary dataset, then parse the formatted
    // string — keeps the bundle total accurate when variants differ in price.
    const fromResource = Number(detail.resource?.price);
    const fromDataset = Number(freshSummary?.dataset.mainPriceCents);
    if (Number.isFinite(fromResource) && fromResource > 0) {
      this.#mainPriceCents = fromResource;
    } else if (Number.isFinite(fromDataset) && fromDataset > 0) {
      this.#mainPriceCents = fromDataset;
    } else if (freshPriceText) {
      const parsed = convertMoneyToMinorUnits(freshPriceText, this.#currency);
      if (parsed !== null) this.#mainPriceCents = parsed;
    }

    this.#syncHeaderPrice(getBundleStore().snapshot());
  };

  #onModelUpdate = () => {
    this.#cacheElements();
    this.#cacheMainPrice();
    this.#syncHeaderPrice(getBundleStore().snapshot());
  };

  /** @param {Event} event */
  #onBundleChange = (event) => {
    const snapshot = /** @type {any} */ (event).detail?.snapshot ?? getBundleStore().snapshot();
    this.#syncHeaderPrice(snapshot);
    this.#renderContributors(snapshot);
  };

  /**
   * Idempotent render with per-contributor, per-item diffing. Each display
   * item is keyed by `variantId` (or its title as fallback), so rows can be
   * added, removed, or updated in-place. Rows that survive the diff stay
   * mounted — enabling enter/leave animations and avoiding flicker when an
   * unrelated contributor publishes. The static main-product row from Liquid
   * is never touched.
   *
   * @param {{ contributors: Array<{ name: string, display: Array<any> }> }} snapshot
   */
  #renderContributors(snapshot) {
    if (!this.#breakdownEl) return;

    const activeNames = new Set(
      snapshot.contributors.filter((c) => c.display.length > 0).map((c) => c.name)
    );

    for (const [name, items] of this.#rowsByContributor) {
      if (activeNames.has(name)) continue;
      for (const { row } of items.values()) this.#animateOutAndRemove(row);
      this.#rowsByContributor.delete(name);
    }

    for (const c of snapshot.contributors) {
      if (c.display.length === 0) continue;

      let perContributor = this.#rowsByContributor.get(c.name);
      if (!perContributor) {
        perContributor = new Map();
        this.#rowsByContributor.set(c.name, perContributor);
      }

      const nextKeys = new Set();

      for (const display of c.display) {
        const key = this.#displayKey(display);
        const signature = JSON.stringify(display);
        nextKeys.add(key);

        const existing = perContributor.get(key);
        if (existing) {
          if (existing.signature !== signature) {
            this.#updateRow(existing.row, display);
            existing.signature = signature;
          }
          continue;
        }

        const row = this.#buildRow(c.name, display);
        this.#breakdownEl.appendChild(row);
        if (prefersReducedMotion()) {
          perContributor.set(key, { row, signature });
          continue;
        }
        // Measure the natural height now (row already laid out) so the
        // keyframes can animate from 0 → row-height. Without this, only the
        // opacity / transform would animate while the row's space pops in,
        // which looks unfinished against the smooth height-collapse on remove.
        // `--row-height` is intentionally left set after cleanup so a
        // back-to-back remove animation (which also reads `--row-height`)
        // isn't broken by a still-pending is-adding cleanup.
        row.style.setProperty('--row-height', `${row.scrollHeight}px`);
        row.classList.add('is-adding');
        onAnimationEnd(row, () => row.classList.remove('is-adding'));
        perContributor.set(key, { row, signature });
      }

      for (const [key, { row }] of perContributor) {
        if (nextKeys.has(key)) continue;
        this.#animateOutAndRemove(row);
        perContributor.delete(key);
      }
    }
  }

  /**
   * @param {{ variantId?: string, title: string }} display
   * @returns {string}
   */
  #displayKey(display) {
    return display.variantId ?? display.title;
  }

  /** @param {HTMLLIElement} row */
  #animateOutAndRemove(row) {
    const remove = () => row.remove();
    if (prefersReducedMotion()) return remove();
    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('is-removing');
    onAnimationEnd(row, remove);
  }

  /**
   * @param {HTMLLIElement} row
   * @param {{ title: string, subtitle?: string, priceHtml?: string }} display
   */
  #updateRow(row, display) {
    const label = row.querySelector('.details-order-summary__item-label');
    const value = row.querySelector('.details-order-summary__item-value');
    if (label) {
      label.textContent = display.subtitle ? `${display.title} · ${display.subtitle}` : display.title;
    }
    if (value && display.priceHtml) {
      value.innerHTML = display.priceHtml;
    }
  }

  /**
   * @param {string} contributorName
   * @param {{ role: string, title: string, subtitle?: string, priceHtml?: string, variantId?: string }} display
   * @returns {HTMLLIElement}
   */
  #buildRow(contributorName, display) {
    const li = document.createElement('li');
    li.className = `details-order-summary__item details-order-summary__item--${display.role}`;
    li.dataset.contributor = contributorName;
    if (display.variantId) li.dataset.variantId = display.variantId;

    const label = document.createElement('span');
    label.className = 'details-order-summary__item-label';
    label.textContent = display.subtitle ? `${display.title} · ${display.subtitle}` : display.title;
    li.appendChild(label);

    if (display.priceHtml) {
      const value = document.createElement('span');
      value.className = 'details-order-summary__item-value';
      value.innerHTML = display.priceHtml;
      li.appendChild(value);
    }

    return li;
  }
}

if (!customElements.get('order-summary')) {
  customElements.define('order-summary', OrderSummary);
}
